import { ArrowRight, Quote } from "lucide-react";
import { BRAND, Reveal } from "./shared";

export function Coach({ onBookClick }) {
  return (
    <section id="coach" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative">
              <img
                src={BRAND.coach}
                alt="Aditya Kulhari"
                className="w-full h-[520px] md:h-[620px] object-cover"
                data-testid="coach-image"
              />
              <div className="absolute -top-4 -right-4 w-24 h-24 border border-brand-accent hidden md:block" />
            </div>
          </Reveal>

          <div className="lg:col-span-7 order-1 lg:order-2">
            <Reveal><p className="eyebrow">Meet your coach</p></Reveal>
            <Reveal delay={80}>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mt-6 text-brand-ink">
                Aditya Kulhari
              </h2>
            </Reveal>
            <Reveal delay={140}>
              <p className="mt-3 font-sans text-brand-accent tracking-wide uppercase text-sm">
                Strength Coach · Former National-Level Boxer
              </p>
            </Reveal>
            <Reveal delay={200}>
              <div className="mt-8 space-y-5 text-base md:text-lg text-brand-ink2 leading-relaxed font-sans max-w-2xl">
                <p>
                  Aditya Kulhari is a national-level boxer with{" "}
                  <span className="text-brand-ink font-medium">over nine years of experience</span> in the fitness industry.
                </p>
                <p>
                  Before founding Har Ghar Strength, he led fitness operations
                  at <span className="text-brand-accent font-medium">Cult.fit</span>, playing a key role in launching
                  and scaling operations across cities including Mumbai, Pune,
                  Ahmedabad, Surat and Indore while managing and mentoring a
                  team of around <span className="text-brand-ink font-medium">150 trainers</span>.
                </p>
                <p>
                  He later became the first fitness hire at{" "}
                  <span className="text-brand-accent font-medium">Gabit</span>, where he built the fitness vertical
                  from the ground up — designing online fitness programs,
                  creating strength training frameworks and SOPs, developing
                  workout systems, and contributing domain expertise to train
                  the company's AI-powered fitness platform.
                </p>
                <p>
                  Today, he brings this experience in coaching, leadership,
                  and fitness system design to help individuals and
                  organisations build sustainable strength, improve health,
                  and make fitness a lifelong habit.
                </p>
              </div>
            </Reveal>
            <Reveal delay={280}>
              <div className="mt-10">
                <button onClick={onBookClick} className="btn-accent" data-testid="coach-book-btn">
                  Train with Aditya
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    quote:
      "I hadn't trained in years. Aditya built something I could actually stick to at home with two dumbbells. Six months in — I'm stronger than I've ever been.",
    name: "Rohan Sharma",
    detail: "Product Manager · Bengaluru",
  },
  {
    quote:
      "The check-ins made all the difference. It didn't feel like a program, it felt like having a coach who genuinely cared about how I was doing.",
    name: "Neha Verma",
    detail: "Doctor · Delhi",
  },
  {
    quote:
      "Lost 9 kg without ever setting foot in a gym. Aditya focuses on habits, not hype. That's why it stuck.",
    name: "Karan Iyer",
    detail: "Founder · Mumbai",
  },
  {
    quote:
      "I travel a lot for work. My workouts finally travel with me. Rooftop, hotel room, park — it all works.",
    name: "Ananya Rao",
    detail: "Consultant · Hyderabad",
  },
];

export function Testimonials() {
  return (
    <section id="results" className="bg-brand-surfaceAlt py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal><p className="eyebrow">Real results, real people</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight mt-6 text-brand-ink">
              Trusted by people who
              <br />
              train <em className="italic text-brand-accent">from anywhere.</em>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 80}>
              <figure
                className="bg-brand-surface border border-brand-border p-8 md:p-10 h-full flex flex-col justify-between"
                data-testid={`testimonial-${i}`}
              >
                <Quote className="h-6 w-6 text-brand-accent" />
                <blockquote className="mt-6 font-serif text-xl md:text-2xl leading-snug text-brand-ink">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8 pt-6 border-t border-brand-border">
                  <div className="font-medium text-brand-ink">{t.name}</div>
                  <div className="text-sm text-brand-ink2 mt-0.5">{t.detail}</div>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
